import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import GuppyCard from './GuppyCard';
import PlantCard from './PlantCard';
import AquariumCard from './AquariumCard';
import FoodCard from './FoodCard';
import OrderDialog from './OrderDialog';
import QuickViewModal from './QuickViewModal';
import SkeletonLoader from './SkeletonLoader';
import CustomDrawer from './CustomDrawer';
import CustomAppBar from './CustomAppBar';
import LoadingScreen from './loadingscreen';
import WelcomePopup from './WelcomePopup';
import ScrollFish from './ScrollFish';
import { useNavigate } from 'react-router-dom';
import { FaComment, FaFilter, FaHeart, FaTimes, FaFish, FaLeaf, FaGlassWhiskey, FaChevronLeft, FaChevronRight, FaSearch, FaArrowUp, FaSortAmountDown, FaSortAmountUp, FaUtensils, FaPalette } from 'react-icons/fa';
import { extractGuppyColors, matchesColor, getAvailableColors, COLOR_TRANSLATIONS, COLOR_HEX_MAP, searchMatchesColor } from '../services/colorDetectionService';
import './HomeScreen.css';

const HomeScreen = () => {
  const [guppies, setGuppies] = useState([]);
  const [plants, setPlants] = useState([]);
  const [aquariums, setAquariums] = useState([]);
  const [foods, setFoods] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true);

  // New State for Priority Features
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortOption, setSortOption] = useState('default'); // default, price-low, price-high, newest, stock
  const [quickViewItem, setQuickViewItem] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);


  // Color Filter State
  const [selectedColor, setSelectedColor] = useState('all');
  const [colorExtracting, setColorExtracting] = useState(false);

  // Order Dialog State
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const tabsRef = useRef(null);
  const searchInputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize localStorage only once when component mounts
    if (!localStorage.getItem('orderIds')) {
      localStorage.setItem('orderIds', '[]');
    }
  }, []);

  // Function to handle opening order dialog
  const handleOrderClick = (item) => {
    setSelectedItem(item);
    setOrderDialogOpen(true);
  };

  // Function to handle closing order dialog
  const handleOrderClose = () => {
    setOrderDialogOpen(false);
    setSelectedItem(null);
  };

  // Extract colors for guppies (with improved performance)
  const extractColorsForGuppies = useCallback(async (guppiesList) => {
    setColorExtracting(true);
    try {
      // Process in batches to avoid blocking
      const batchSize = 3;
      const guppiesWithColors = [];

      for (let i = 0; i < guppiesList.length; i += batchSize) {
        const batch = guppiesList.slice(i, i + batchSize);
        const batchResults = await Promise.all(
          batch.map(async (guppy) => {
            // Check if colors already exist
            if (guppy.colors && Array.isArray(guppy.colors) && guppy.colors.length > 0) {
              return guppy;
            }

            // Extract colors from images (will use cache if available)
            const colors = await extractGuppyColors(guppy);
            return { ...guppy, colors: colors.length > 0 ? colors : [] };
          })
        );
        guppiesWithColors.push(...batchResults);

        // Small delay between batches to keep UI responsive
        if (i + batchSize < guppiesList.length) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      setColorExtracting(false);
      return guppiesWithColors;
    } catch (error) {
      console.error('Error extracting colors:', error);
      setColorExtracting(false);
      return guppiesList;
    }
  }, []);

  // Fetch guppies
  useEffect(() => {
    const guppyRef = collection(db, 'guppies');
    const unsubscribe = onSnapshot(guppyRef, (querySnapshot) => {
      const guppiesList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        type: 'guppy',
        ...doc.data(),
      }));

      // Sort immediately based on sold status
      const sortGuppies = (list) => {
        return list.sort((a, b) =>
          a.sold_status === b.sold_status ? 0 : a.sold_status ? 1 : -1
        );
      };

      // 1. Set immediate data (fast render)
      setGuppies(sortGuppies(guppiesList));
      setLoading(false); // Dismiss loading screen if guppies arrive

      // Client-side color extraction disabled for performance
      // If color extraction is needed, it should be done on the backend
      // or at the time of guppy creation.
    });
    return () => unsubscribe();
  }, [extractColorsForGuppies]);

  // Fetch plants
  useEffect(() => {
    const plantRef = collection(db, 'plants');
    const unsubscribe = onSnapshot(plantRef,
      (querySnapshot) => {
        const plantsList = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            type: 'plant',
            name: data.name || 'نبات غير مسمى',
            quantity: data.quantity ?? 0,
            price: data.price || 0,
            image: data.image || '',
            sold_status: data.sold_status || false,
            tags: data.tags || '0',
            care: data.care || 'غير معروف'
          };
        });
        setPlants(plantsList);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching plants:", error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Fetch aquariums
  useEffect(() => {
    const aquariumRef = collection(db, 'aquariums');
    const unsubscribe = onSnapshot(aquariumRef, (querySnapshot) => {
      const aquariumsList = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          type: 'aquarium',
          name: data.name || 'حوض غير مسمى',
          quantity: data.quantity ?? 0,
          price: data.price || 0,
          image: data.image || '',
          sold_status: data.sold_status || false,
          tags: data.tags || '0',
          care: data.care || 'غير معروف',
          size: data.size || 'غير محدد',
          material: data.material || 'غير محدد'
        };
      });
      setAquariums(aquariumsList);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch food
  useEffect(() => {
    const foodRef = collection(db, 'food');
    const unsubscribe = onSnapshot(foodRef, (querySnapshot) => {
      const foodsList = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          type: 'food',
          name: data.name || 'طعام غير مسمى',
          flakesPrice: data.flakesPrice || 0,
          powderPrice: data.powderPrice || 0,
          flakesQuantity: data.flakesQuantity ?? 0,
          powderQuantity: data.powderQuantity ?? 0,
          image: data.image || '',
          sold_status: data.sold_status || false,
        };
      });
      setFoods(foodsList);
    });
    return () => unsubscribe();
  }, []);

  // Get item price for sorting
  const getItemPrice = useCallback((item) => {
    if (item.type === 'guppy') {
      if (item.couplecount > 0 && item.coupleprice) return item.coupleprice;
      if (item.malecount > 0 && item.maleprice) return item.maleprice;
      if (item.femalecount > 0 && item.femaleprice) return item.femaleprice;
      return 0;
    }
    if (item.type === 'food') {
      // Return the lower price or available price
      if (item.flakesPrice > 0 && item.powderPrice > 0) {
        return Math.min(item.flakesPrice, item.powderPrice);
      }
      return item.flakesPrice || item.powderPrice || 0;
    }
    return item.price || 0;
  }, []);

  // Filter items based on category, type, search, and color
  const filteredItems = useMemo(() => {
    let allItems = [...guppies, ...plants, ...aquariums, ...foods];

    if (showFavoritesOnly) {
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
      allItems = allItems.filter(item => favorites.includes(item.id));
    }

    // Filter by type first
    if (selectedType !== 'all') {
      allItems = allItems.filter(item => item.type === selectedType);
    }

    // Filter by color (ONLY applies to guppies - never filter plants/aquariums/foods)
    if (selectedColor !== 'all') {
      allItems = allItems.filter(item => {
        // Only apply color filter to guppies
        if (item.type === 'guppy') {
          return matchesColor(item, selectedColor);
        }
        // Always keep non-guppy items regardless of color filter
        return true;
      });
    }

    // Filter by search query (using debounced value)
    // Search can match both name and color (for guppies)
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase().trim();
      // Check if the search query matches a color name
      const matchedColor = searchMatchesColor(query);

      allItems = allItems.filter(item => {
        // First check if name matches
        const nameMatches = item.name && item.name.toLowerCase().includes(query);

        // For guppies, also check if search matches a color
        if (item.type === 'guppy' && matchedColor) {
          const colorMatches = matchesColor(item, matchedColor);
          return nameMatches || colorMatches;
        }

        // For other items, only check name
        return nameMatches;
      });
    }

    // Then filter by category if selected (case insensitive partial match)
    if (selectedCategory) {
      allItems = allItems.filter(item =>
        item.name && item.name.toLowerCase().includes(selectedCategory.toLowerCase())
      );
    }

    return allItems;
  }, [showFavoritesOnly, selectedCategory, selectedType, selectedColor, debouncedSearch, guppies, plants, aquariums, foods]);

  // Sort items based on sort option
  const sortedItems = useMemo(() => {
    let sorted = [...filteredItems];

    switch (sortOption) {
      case 'price-low':
        sorted.sort((a, b) => getItemPrice(a) - getItemPrice(b));
        break;
      case 'price-high':
        sorted.sort((a, b) => getItemPrice(b) - getItemPrice(a));
        break;
      case 'newest':
        // Assuming items are already sorted by newest from Firestore
        // Could add timestamp field if needed
        break;
      case 'stock':
        sorted.sort((a, b) => {
          let aStock = 0;
          let bStock = 0;

          if (a.type === 'guppy') {
            aStock = (a.couplecount || 0) + (a.malecount || 0) + (a.femalecount || 0);
          } else if (a.type === 'food') {
            aStock = (a.flakesQuantity || 0) + (a.powderQuantity || 0);
          } else {
            aStock = a.quantity || 0;
          }

          if (b.type === 'guppy') {
            bStock = (b.couplecount || 0) + (b.malecount || 0) + (b.femalecount || 0);
          } else if (b.type === 'food') {
            bStock = (b.flakesQuantity || 0) + (b.powderQuantity || 0);
          } else {
            bStock = b.quantity || 0;
          }

          return bStock - aStock;
        });
        break;
      case 'default':
      default:
        // Default: available items first, then sold out
        sorted.sort((a, b) => {
          if (a.sold_status === b.sold_status) return 0;
          return a.sold_status ? 1 : -1;
        });
        break;
    }

    return sorted;
  }, [filteredItems, sortOption, getItemPrice]);

  // Get item counts for each type
  const getItemCounts = () => {
    const counts = {
      all: guppies.length + plants.length + aquariums.length + foods.length,
      guppy: guppies.length,
      plant: plants.length,
      aquarium: aquariums.length,
      food: foods.length
    };
    return counts;
  };

  const itemCounts = getItemCounts();

  // Type filter options
  const typeFilters = [
    { key: 'all', label: 'الكل', icon: FaFilter, color: '#10b981' },
    { key: 'guppy', label: 'الأسماك', icon: FaFish, color: '#06b6d4' },
    { key: 'plant', label: 'النباتات', icon: FaLeaf, color: '#22c55e' },
    { key: 'aquarium', label: 'الأحواض', icon: FaGlassWhiskey, color: '#8b5cf6' },
    { key: 'food', label: 'طعام الأسماك', icon: FaUtensils, color: '#f97316' }
  ];

  // Handle scroll events for tabs
  const handleScroll = (e) => {
    const container = e.target;
    setShowLeftScroll(container.scrollLeft > 0);
    setShowRightScroll(container.scrollLeft + container.clientWidth < container.scrollWidth);
  };

  // Initialize scroll indicators
  useEffect(() => {
    if (tabsRef.current) {
      const container = tabsRef.current;
      setShowRightScroll(container.scrollWidth > container.clientWidth);

      // Recheck on window resize
      const handleResize = () => {
        setShowLeftScroll(container.scrollLeft > 0);
        setShowRightScroll(container.scrollLeft + container.clientWidth < container.scrollWidth);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Handle scroll for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Get active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedType !== 'all') count++;
    if (selectedCategory) count++;
    if (searchQuery.trim()) count++;
    if (showFavoritesOnly) count++;
    if (sortOption !== 'default') count++;
    if (selectedColor !== 'all') count++;
    return count;
  };

  // Get available colors from guppies
  const availableColors = getAvailableColors(guppies);

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedType('all');
    setSelectedCategory('');
    setSearchQuery('');
    setShowFavoritesOnly(false);
    setSortOption('default');
    setSelectedColor('all');
  };

  // Handle quick view
  const handleQuickView = (item) => {
    setQuickViewItem(item);
  };

  // Handle view details navigation
  const handleViewDetails = (item) => {
    if (item.type === 'guppy') {
      navigate('/guppy-details', { state: { guppy: item } });
    } else if (item.type === 'aquarium') {
      navigate('/aquarium-details', { state: { aquarium: item } });
    }
  };

  // Debounced search (simple implementation)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset color filter when switching away from guppies
  useEffect(() => {
    if (selectedType !== 'all' && selectedType !== 'guppy' && selectedColor !== 'all') {
      setSelectedColor('all');
    }
  }, [selectedType, selectedColor]);

  const footerLinkStyle = {
    background: 'none',
    border: 'none',
    color: '#cbd5e1',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    padding: '10px 20px',
    borderRadius: '12px',
    fontFamily: 'inherit'
  };

  if (loading) return <LoadingScreen />;


  return (
    <div style={{
      minHeight: '100vh',
      position: 'relative',
      width: '100%',
      overflowX: 'hidden',
      background: 'transparent'
    }}>
      {/* Welcome Popup */}
      <WelcomePopup />

      {/* AppBar floats on top of the 3D hero */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100 }}>
        <CustomAppBar onMenuClick={() => setDrawerOpen(true)} />
      </div>

      {/* 3D Hero Section — full viewport fish scene */}
      <ScrollFish />

      {/* ── Shop Section Scroll Target ── */}
      <div id="shop-section" style={{ position: 'relative', top: '-80px' }} />

      {/* Sticky Category Tabs Only */}
      <div className="sticky-categories-bar" style={{
        background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.45) 0%, rgba(15, 23, 42, 0.3) 100%)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(51, 65, 85, 0.4)',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '18px 24px',
          width: '100%',
          boxSizing: 'border-box',
          position: 'relative'
        }}>
          {/* Subtle top accent line */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.6), rgba(6, 182, 212, 0.6), transparent)',
            opacity: 0.8
          }}></div>
          {/* Type Filter Tabs with Scroll Indicators */}
          <div style={{
            position: 'relative'
          }}>
            {/* Left Scroll Indicator */}
            {showLeftScroll && (
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: '40px',
                background: 'linear-gradient(90deg, rgba(15, 23, 42, 0.4), transparent)',
                zIndex: 1,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingLeft: '8px'
              }}>
                <FaChevronLeft color="#94a3b8" size={16} />
              </div>
            )}

            {/* Right Scroll Indicator */}
            {showRightScroll && (
              <div style={{
                position: 'absolute',
                right: 0,
                top: 0,
                height: '100%',
                width: '40px',
                background: 'linear-gradient(270deg, rgba(15, 23, 42, 0.4), transparent)',
                zIndex: 1,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: '8px'
              }}>
                <FaChevronRight color="#94a3b8" size={16} />
              </div>
            )}

            <div
              ref={tabsRef}
              onScroll={handleScroll}
              style={{
                display: 'flex',
                gap: '12px',
                overflowX: 'auto',
                paddingBottom: '4px',
                paddingTop: '4px',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                scrollBehavior: 'smooth',
                alignItems: 'center'
              }}
            >
              {typeFilters.map(filter => {
                const IconComponent = filter.icon;
                const isActive = selectedType === filter.key;
                return (
                  <button
                    key={filter.key}
                    onClick={() => setSelectedType(filter.key)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      padding: '14px 24px',
                      borderRadius: '28px',
                      border: `1.5px solid ${isActive ? filter.color : 'rgba(71, 85, 105, 0.5)'}`,
                      fontSize: '15px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                      whiteSpace: 'nowrap',
                      background: isActive
                        ? `linear-gradient(135deg, ${filter.color}ed, ${filter.color}d0)`
                        : 'linear-gradient(135deg, rgba(51, 65, 85, 0.7), rgba(71, 85, 105, 0.5))',
                      backdropFilter: 'blur(20px)',
                      color: isActive ? 'white' : '#e2e8f0',
                      transform: isActive ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
                      boxShadow: isActive
                        ? `0 10px 32px ${filter.color}60, 0 0 24px ${filter.color}40, inset 0 1px 0 rgba(255,255,255,0.25)`
                        : '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
                      flexShrink: 0,
                      position: 'relative',
                      overflow: 'hidden',
                      letterSpacing: '0.01em'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.transform = 'translateY(-3px) scale(1.03)';
                        e.currentTarget.style.borderColor = filter.color + '90';
                        e.currentTarget.style.boxShadow = `0 8px 24px ${filter.color}40`;
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(51, 65, 85, 0.85), rgba(71, 85, 105, 0.65))';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.borderColor = 'rgba(71, 85, 105, 0.5)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(51, 65, 85, 0.7), rgba(71, 85, 105, 0.5))';
                      }
                    }}
                  >
                    {/* Glow effect for active */}
                    {isActive && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: `radial-gradient(circle at center, ${filter.color}50, transparent 75%)`,
                        animation: 'pulse 2.5s ease-in-out infinite',
                        zIndex: 0
                      }}></div>
                    )}
                    <IconComponent
                      size={20}
                      style={{
                        position: 'relative',
                        zIndex: 1,
                        filter: isActive ? `drop-shadow(0 3px 10px ${filter.color}90)` : 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    <span style={{
                      position: 'relative',
                      zIndex: 1,
                      fontWeight: '700',
                      letterSpacing: '0.02em'
                    }}>
                      {filter.label}
                    </span>
                    <span style={{
                      background: isActive
                        ? 'rgba(255,255,255,0.3)'
                        : 'rgba(255,255,255,0.12)',
                      padding: '5px 12px',
                      borderRadius: '14px',
                      fontSize: '13px',
                      fontWeight: '800',
                      position: 'relative',
                      zIndex: 1,
                      border: `1px solid ${isActive ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)'}`,
                      boxShadow: isActive
                        ? 'inset 0 1px 3px rgba(0,0,0,0.25), 0 1px 2px rgba(255,255,255,0.1)'
                        : 'inset 0 1px 2px rgba(0,0,0,0.2)',
                      minWidth: '32px',
                      textAlign: 'center',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    }}>
                      {itemCounts[filter.key]}
                    </span>
                    {/* Shimmer effect on active */}
                    {isActive && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                        animation: 'shimmer 2.5s ease-in-out infinite',
                        zIndex: 1
                      }}></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div style={{
        background: 'rgba(15, 23, 42, 0.25)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(51, 65, 85, 0.35)',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '24px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Ultra-Enhanced Search Bar */}
          <div style={{
            marginBottom: '24px',
            position: 'relative'
          }}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3))'
            }}>
              {/* Search Glow Effect */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(59, 130, 246, 0.3))',
                borderRadius: '16px',
                filter: 'blur(20px)',
                opacity: 0,
                transition: 'opacity 0.3s ease',
                zIndex: 0
              }} className="search-glow"></div>

              <FaSearch
                style={{
                  position: 'absolute',
                  right: '20px',
                  color: searchQuery ? '#10b981' : '#64748b',
                  zIndex: 2,
                  transition: 'all 0.3s ease',
                  filter: searchQuery ? 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.6))' : 'none'
                }}
                size={20}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="🔍 ابحث عن منتج..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '18px 60px 18px 24px',
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.45), rgba(30, 41, 59, 0.3))',
                  backdropFilter: 'blur(20px)',
                  border: `1.5px solid ${searchQuery ? 'rgba(16, 185, 129, 0.6)' : 'rgba(71, 85, 105, 0.5)'}`,
                  borderRadius: '20px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '500',
                  direction: 'rtl',
                  textAlign: 'right',
                  outline: 'none',
                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: searchQuery
                    ? '0 10px 40px rgba(16, 185, 129, 0.25), inset 0 0 0 1px rgba(16, 185, 129, 0.4)'
                    : '0 6px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
                  position: 'relative',
                  zIndex: 1,
                  letterSpacing: '0.01em'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(16, 185, 129, 0.85)';
                  e.target.style.background = 'linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.4))';
                  e.target.style.boxShadow = '0 14px 48px rgba(16, 185, 129, 0.35), inset 0 0 0 1px rgba(16, 185, 129, 0.5)';
                  e.target.style.transform = 'scale(1.01)';
                  const glowEl = document.querySelector('.search-glow');
                  if (glowEl) glowEl.style.opacity = '1';
                }}
                onBlur={(e) => {
                  if (!searchQuery) {
                    e.target.style.borderColor = 'rgba(71, 85, 105, 0.5)';
                    e.target.style.background = 'linear-gradient(135deg, rgba(15, 23, 42, 0.45), rgba(30, 41, 59, 0.3))';
                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)';
                    e.target.style.transform = 'scale(1)';
                    const glowEl = document.querySelector('.search-glow');
                    if (glowEl) glowEl.style.opacity = '0';
                  }
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    left: '16px',
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3))',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#f87171',
                    transition: 'all 0.3s ease',
                    zIndex: 2,
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1) rotate(90deg)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.5), rgba(220, 38, 38, 0.5))';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3))';
                  }}
                >
                  <FaTimes size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Active Filter Chips */}
          {getActiveFiltersCount() > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              marginBottom: '20px',
              alignItems: 'center'
            }}>
              {selectedType !== 'all' && (
                <div className="filter-chip">
                  <span>{typeFilters.find(f => f.key === selectedType)?.label}</span>
                  <button onClick={() => setSelectedType('all')}>
                    <FaTimes size={12} />
                  </button>
                </div>
              )}
              {selectedCategory && (
                <div className="filter-chip">
                  <span>{selectedCategory}</span>
                  <button onClick={() => setSelectedCategory('')}>
                    <FaTimes size={12} />
                  </button>
                </div>
              )}
              {searchQuery.trim() && (
                <div className="filter-chip">
                  <span>بحث: {searchQuery}</span>
                  <button onClick={() => setSearchQuery('')}>
                    <FaTimes size={12} />
                  </button>
                </div>
              )}
              {showFavoritesOnly && (
                <div className="filter-chip">
                  <span>المفضلة فقط</span>
                  <button onClick={() => setShowFavoritesOnly(false)}>
                    <FaTimes size={12} />
                  </button>
                </div>
              )}
              {sortOption !== 'default' && (
                <div className="filter-chip">
                  <span>
                    {sortOption === 'price-low' && 'السعر: منخفض إلى عالي'}
                    {sortOption === 'price-high' && 'السعر: عالي إلى منخفض'}
                    {sortOption === 'newest' && 'الأحدث'}
                    {sortOption === 'stock' && 'المخزون'}
                  </span>
                  <button onClick={() => setSortOption('default')}>
                    <FaTimes size={12} />
                  </button>
                </div>
              )}
              {selectedColor !== 'all' && (
                <div className="filter-chip">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: COLOR_HEX_MAP[selectedColor] || '#64748b',
                      border: '2px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}></div>
                    {COLOR_TRANSLATIONS[selectedColor] || selectedColor}
                  </span>
                  <button onClick={() => setSelectedColor('all')}>
                    <FaTimes size={12} />
                  </button>
                </div>
              )}
              {getActiveFiltersCount() > 1 && (
                <button
                  onClick={clearAllFilters}
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '20px',
                    color: '#f87171',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <FaTimes size={12} />
                  إزالة الكل
                </button>
              )}
            </div>
          )}

          {/* Category and Action Buttons */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            {/* Sort Dropdown */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flex: 1,
              minWidth: '220px'
            }}>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                style={{
                  padding: '12px 18px',
                  background: 'rgba(30, 41, 59, 0.9)',
                  border: '1.5px solid rgba(71, 85, 105, 0.5)',
                  borderRadius: '14px',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  outline: 'none',
                  direction: 'rtl',
                  flex: 1,
                  maxWidth: '280px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(16, 185, 129, 0.6)';
                  e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255,255,255,0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(71, 85, 105, 0.5)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)';
                }}
              >
                <option value="default">الترتيب الافتراضي</option>
                <option value="price-low">السعر: منخفض إلى عالي</option>
                <option value="price-high">السعر: عالي إلى منخفض</option>
                <option value="newest">الأحدث</option>
                <option value="stock">حسب المخزون</option>
              </select>
            </div>

            {/* Color Filter (only filters guppies, but can be visible when viewing all or guppies) */}
            {/* Enhanced Color Filter Swatches */}
            {(selectedType === 'all' || selectedType === 'guppy') && availableColors.length > 0 && (
              <div className="color-filter-container" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flex: '1 1 auto',
                overflowX: 'auto',
                padding: '4px 2px',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                maskImage: 'linear-gradient(to right, black 90%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent 100%)'
              }}>
                <style>
                  {`
                    .color-filter-container::-webkit-scrollbar {
                      display: none;
                    }
                  `}
                </style>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#e2e8f0',
                  fontSize: '14px',
                  fontWeight: '700',
                  whiteSpace: 'nowrap',
                  paddingLeft: '4px'
                }}>
                  <FaPalette size={16} color="#8b5cf6" />
                  <span className="hide-on-mobile">اللون:</span>
                </div>

                {/* All Colors Button */}
                <button
                  onClick={() => setSelectedColor('all')}
                  title="جميع الألوان"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    border: selectedColor === 'all'
                      ? '2px solid #fff'
                      : '2px solid rgba(71, 85, 105, 0.5)',
                    background: 'conic-gradient(from 0deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #a855f7, #ef4444)',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: selectedColor === 'all' ? 'scale(1.15)' : 'scale(1)',
                    boxShadow: selectedColor === 'all'
                      ? '0 0 0 2px rgba(255, 255, 255, 0.2), 0 8px 20px rgba(0, 0, 0, 0.5)'
                      : '0 2px 8px rgba(0,0,0,0.3)',
                    position: 'relative',
                    flexShrink: 0
                  }}
                >
                  {selectedColor === 'all' && (
                    <div style={{
                      position: 'absolute',
                      inset: '-4px',
                      borderRadius: '50%',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      animation: 'pulse 2s infinite'
                    }}></div>
                  )}
                </button>

                {/* Individual Color Swatches */}
                {availableColors.map(color => {
                  const isSelected = selectedColor === color;
                  const hexColor = COLOR_HEX_MAP[color] || '#64748b';
                  const isLight = ['white', 'yellow', 'gold', 'cyan', 'turquoise'].includes(color);

                  return (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      title={COLOR_TRANSLATIONS[color] || color}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: isSelected
                          ? `2px solid ${isLight ? '#0f172a' : '#fff'}`
                          : '2px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: hexColor,
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isSelected ? 'scale(1.15) translateY(-2px)' : 'scale(1)',
                        boxShadow: isSelected
                          ? `0 0 0 2px ${hexColor}60, 0 8px 20px ${hexColor}40`
                          : '0 2px 8px rgba(0,0,0,0.2)',
                        position: 'relative',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {isSelected && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: isLight ? '#0f172a' : '#fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}></div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexShrink: 0
            }}>
              <button
                aria-label="Toggle favorites"
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 20px',
                  borderRadius: '14px',
                  fontSize: '15px',
                  fontWeight: '700',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                  background: showFavoritesOnly
                    ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                    : 'rgba(51, 65, 85, 0.7)',
                  color: showFavoritesOnly ? 'white' : '#e2e8f0',
                  boxShadow: showFavoritesOnly
                    ? '0 6px 20px rgba(239, 68, 68, 0.45)'
                    : '0 4px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)',
                  transform: showFavoritesOnly ? 'translateY(-2px)' : 'translateY(0)',
                  backdropFilter: 'blur(10px)',
                  letterSpacing: '0.01em'
                }}
                onMouseEnter={(e) => {
                  if (!showFavoritesOnly) {
                    e.currentTarget.style.background = 'rgba(51, 65, 85, 0.85)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showFavoritesOnly) {
                    e.currentTarget.style.background = 'rgba(51, 65, 85, 0.7)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <FaHeart size={14} />
                <span className="hide-on-mobile">
                  {showFavoritesOnly ? 'إخفاء المفضلة' : 'المفضلة فقط'}
                </span>
              </button>

              {selectedCategory && (
                <button
                  aria-label="Remove filter"
                  onClick={() => setSelectedCategory('')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    borderRadius: '14px',
                    fontSize: '15px',
                    fontWeight: '700',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    whiteSpace: 'nowrap',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: 'white',
                    boxShadow: '0 6px 20px rgba(245, 158, 11, 0.45)',
                    transform: 'translateY(-2px)',
                    backdropFilter: 'blur(10px)',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    letterSpacing: '0.01em'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(245, 158, 11, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.45)';
                  }}
                >
                  <FaTimes size={14} />
                  <span className="hide-on-mobile">إزالة التصفية</span>
                </button>
              )}

              <button
                aria-label="Filter items"
                onClick={() => setDrawerOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 20px',
                  borderRadius: '14px',
                  fontSize: '15px',
                  fontWeight: '700',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  boxShadow: '0 6px 20px rgba(16, 185, 129, 0.45)',
                  transform: 'translateY(-2px)',
                  backdropFilter: 'blur(10px)',
                  letterSpacing: '0.01em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.45)';
                }}
              >
                <FaFilter size={14} />
                <span className="hide-on-mobile">تصفية</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <CustomDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCategorySelect={(category) => {
          setSelectedCategory(category);
          setDrawerOpen(false);
        }}
      />

      {/* Enhanced Content Grid */}
      <div className="grid-container" ref={scrollContainerRef}>
        {loading ? (
          <SkeletonLoader count={6} />
        ) : sortedItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7), rgba(51, 65, 85, 0.6))',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '56px 40px',
              maxWidth: '560px',
              margin: '0 auto',
              border: '1.5px solid rgba(51, 65, 85, 0.5)',
              width: '100%',
              boxSizing: 'border-box',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.05)'
            }}>
              <div style={{
                fontSize: '7rem',
                marginBottom: '40px',
                animation: 'float 3s ease-in-out infinite',
                filter: 'drop-shadow(0 12px 40px rgba(16, 185, 129, 0.4))'
              }}>🔍</div>
              <h3 style={{
                fontSize: 'clamp(1.75rem, 5vw, 2.25rem)',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #10b981, #06b6d4)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '20px',
                letterSpacing: '-0.03em',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                lineHeight: '1.2'
              }}>لم يتم العثور على أي منتج</h3>
              <p style={{
                color: '#cbd5e1',
                margin: '0 0 40px 0',
                fontSize: 'clamp(1.0625rem, 2.5vw, 1.25rem)',
                lineHeight: '1.8',
                maxWidth: '450px',
                marginLeft: 'auto',
                marginRight: 'auto',
                fontWeight: '500',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                {getActiveFiltersCount() > 0
                  ? 'جرب تغيير التصفية أو البحث عن منتج آخر 🎯'
                  : 'لا توجد منتجات متوفرة حالياً. تحقق مرة أخرى قريباً! ✨'}
              </p>
              {getActiveFiltersCount() > 0 && (
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    onClick={clearAllFilters}
                    style={{
                      padding: '14px 28px',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      border: 'none',
                      borderRadius: '16px',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      letterSpacing: '0.01em',
                      boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-3px)';
                      e.target.style.boxShadow = '0 10px 28px rgba(16, 185, 129, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                    }}
                  >
                    إزالة جميع التصفيات
                  </button>
                  <button
                    onClick={() => setSelectedType('all')}
                    style={{
                      padding: '14px 28px',
                      background: 'rgba(51, 65, 85, 0.7)',
                      border: '1.5px solid rgba(71, 85, 105, 0.5)',
                      borderRadius: '16px',
                      color: '#e2e8f0',
                      fontSize: '16px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      letterSpacing: '0.01em',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(51, 65, 85, 0.85)';
                      e.target.style.transform = 'translateY(-3px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(51, 65, 85, 0.7)';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)';
                    }}
                  >
                    عرض الكل
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="content-grid">
            {sortedItems.map((item, index) => {
              const animationDelay = `${index * 0.08}s`;

              return (
                <div
                  key={item.id}
                  className="product-card-wrapper"
                  style={{
                    animationDelay,
                    opacity: 0,
                    animation: 'fadeInUpScale 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards',
                    transform: 'scale(0.95) translateY(20px)'
                  }}
                  onDoubleClick={() => handleQuickView(item)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                    e.currentTarget.style.zIndex = '10';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.zIndex = '1';
                  }}
                >
                  {item.type === 'guppy' && (
                    <GuppyCard
                      guppy={item}
                      onOrderClick={() => handleOrderClick(item)}
                    />
                  )}
                  {item.type === 'plant' && (
                    <PlantCard
                      plant={item}
                      onOrderClick={() => handleOrderClick(item)}
                    />
                  )}
                  {item.type === 'aquarium' && (
                    <AquariumCard
                      aquarium={item}
                      onOrderClick={() => handleOrderClick(item)}
                    />
                  )}
                  {item.type === 'food' && (
                    <FoodCard
                      food={item}
                      onOrderClick={() => handleOrderClick(item)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Order Dialog */}
      {orderDialogOpen && selectedItem && (
        <OrderDialog
          item={selectedItem}
          onClose={handleOrderClose}
        />
      )}

      {/* Quick View Modal */}
      {quickViewItem && (
        <QuickViewModal
          item={quickViewItem}
          onClose={() => setQuickViewItem(null)}
          onOrderClick={() => handleOrderClick(quickViewItem)}
          onViewDetails={() => handleViewDetails(quickViewItem)}
        />
      )}

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-button"
          aria-label="العودة للأعلى"
        >
          <FaArrowUp size={20} />
        </button>
      )}

      {/* Enhanced Chat Button */}
      <button
        aria-label="Open chat"
        onClick={() => navigate('/chat')}
        className="wiggle-button"
      >
        <FaComment size={24} />
        <div className="tooltip-text">تحدث معنا</div>
      </button>

      {/* Site Footer */}
      <footer style={{
        marginTop: '80px',
        padding: '60px 24px',
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(16, 185, 129, 0.2)',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10,
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '32px' }}>
             <h2 style={{ 
               fontSize: '2rem', 
               fontWeight: '900', 
               background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
               WebkitBackgroundClip: 'text',
               WebkitTextFillColor: 'transparent',
               backgroundClip: 'text',
               marginBottom: '12px',
               display: 'inline-block'
             }}>Clickfish DZ</h2>
             <p style={{ color: '#94a3b8', fontSize: '1.1rem', fontWeight: '500' }}>متجرك المفضل لأسماك الزينة والنباتات المائية في الجزائر</p>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '12px', 
            flexWrap: 'wrap',
            marginBottom: '40px'
          }}>
            <button 
              onClick={() => navigate('/privacy')} 
              style={footerLinkStyle}
              onMouseEnter={(e) => { e.target.style.color = '#10b981'; e.target.style.background = 'rgba(16, 185, 129, 0.1)'; }}
              onMouseLeave={(e) => { e.target.style.color = '#cbd5e1'; e.target.style.background = 'none'; }}
            >سياسة الخصوصية</button>
            <button 
              onClick={() => navigate('/terms')} 
              style={footerLinkStyle}
              onMouseEnter={(e) => { e.target.style.color = '#10b981'; e.target.style.background = 'rgba(16, 185, 129, 0.1)'; }}
              onMouseLeave={(e) => { e.target.style.color = '#cbd5e1'; e.target.style.background = 'none'; }}
            >الشروط والأحكام</button>
            <button 
              onClick={() => navigate('/data-deletion')} 
              style={footerLinkStyle}
              onMouseEnter={(e) => { e.target.style.color = '#10b981'; e.target.style.background = 'rgba(16, 185, 129, 0.1)'; }}
              onMouseLeave={(e) => { e.target.style.color = '#cbd5e1'; e.target.style.background = 'none'; }}
            >حذف البيانات</button>
          </div>
          
          <div style={{ color: '#64748b', fontSize: '0.9rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '24px' }}>
            © 2024 Clickfish DZ. جميع الحقوق محفوظة.
          </div>
        </div>
      </footer>

      {/* Enhanced Custom Styles */}

      <style>{`
        /* Base styles and overflow prevention */
        * {
          box-sizing: border-box;
        }
        
        html, body {
          overflow-x: hidden;
          width: 100%;
          max-width: 100%;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* Enhanced animations */
        @keyframes fadeInUp {
          from { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        @keyframes fadeInUpScale {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }

        .product-card-wrapper {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
        }

        .product-card-wrapper:hover {
          filter: brightness(1.05);
        }

        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes backgroundMove {
          0%, 100% { transform: perspective(1000px) rotateX(60deg) translateX(0) translateY(0); }
          25% { transform: perspective(1000px) rotateX(60deg) translateX(-20px) translateY(-10px); }
          50% { transform: perspective(1000px) rotateX(60deg) translateX(20px) translateY(10px); }
          75% { transform: perspective(1000px) rotateX(60deg) translateX(-10px) translateY(20px); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-20px) translateX(10px); }
          66% { transform: translateY(-10px) translateX(-10px); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }

        @keyframes slideExpand {
          from {
            width: 0;
            opacity: 0;
          }
          to {
            width: 120px;
            opacity: 1;
          }
        }

        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(3deg); }
          75% { transform: rotate(-3deg); }
        }

        /* Grid Container */
        .grid-container {
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          padding: 40px 24px;
          box-sizing: border-box;
          overflow-x: hidden;
        }

        /* Enhanced Content Grid */
        .content-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
          gap: 28px;
          align-items: stretch;
          justify-items: center;
          width: 100%;
          max-width: 100%;
          padding: 0;
          margin: 0;
          box-sizing: border-box;
        }
        
        .content-grid > * {
          width: 100%;
          max-width: 100%;
          height: auto;
          min-height: fit-content;
          box-sizing: border-box;
          overflow: hidden;
        }

        /* Enhanced Chat Button */
        .wiggle-button {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 30;
          background: linear-gradient(135deg, #10b981, #06b6d4);
          color: white;
          padding: 16px;
          border-radius: 50%;
          border: none;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: wiggle 2s ease-in-out infinite;
        }
        .wiggle-button:hover {
          transform: scale(1.1);
          background: linear-gradient(135deg, #059669, #0891b2);
          box-shadow: 0 30px 60px -12px rgba(16, 185, 129, 0.4);
        }
        .wiggle-button:active {
          transform: scale(0.95);
        }
        .tooltip-text {
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 8px;
          padding: 6px 12px;
          background: #1e293b;
          color: white;
          font-size: 14px;
          border-radius: 8px;
          opacity: 0;
          transition: opacity 0.2s ease;
          white-space: nowrap;
          pointer-events: none;
        }
        .wiggle-button:hover .tooltip-text {
          opacity: 1 !important;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .content-grid {
            grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
            gap: 24px;
          }
        }

        @media (max-width: 1024px) {
          .content-grid {
            grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
            gap: 24px;
          }
          .grid-container {
            padding: 36px 20px;
          }
        }
        
        @media (max-width: 768px) {
          .content-grid {
            grid-template-columns: repeat(auto-fill, minmax(min(260px, 100%), 1fr));
            gap: 20px;
          }
          .grid-container {
            padding: 32px 16px;
          }
        }
        
        @media (max-width: 640px) {
          .hide-on-mobile {
            display: none !important;
          }
          .content-grid {
            grid-template-columns: 1fr;
            gap: 20px;
            justify-items: stretch;
          }
          
          .content-grid > * {
            max-width: none;
            width: 100%;
          }
          
          .grid-container {
            padding: 28px 16px;
          }
        }
        
        @media (max-width: 480px) {
          .content-grid {
            grid-template-columns: 1fr;
            gap: 18px;
          }
          
          .grid-container {
            padding: 24px 12px;
          }
          
          .wiggle-button {
            bottom: 16px;
            right: 16px;
            padding: 12px;
          }
        }
        
        @media (max-width: 360px) {
          .grid-container {
            padding: 20px 10px;
          }
        }

        /* Hide scrollbar for type filter tabs */
        div::-webkit-scrollbar {
          display: none;
        }

        /* Filter Chips */
        .filter-chip {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 16px;
          background: rgba(16, 185, 129, 0.18);
          border: 1.5px solid rgba(16, 185, 129, 0.4);
          border-radius: 22px;
          color: #10b981;
          font-size: 14px;
          font-weight: 700;
          animation: fadeIn 0.3s ease-out;
          letter-spacing: 0.01em;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255,255,255,0.1);
        }

        .filter-chip span {
          color: #10b981;
        }

        .filter-chip button {
          background: rgba(16, 185, 129, 0.25);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 50%;
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #10b981;
          transition: all 0.25s ease;
          padding: 0;
          box-shadow: inset 0 1px 2px rgba(0,0,0,0.2);
        }

        .filter-chip button:hover {
          background: rgba(16, 185, 129, 0.35);
          border-color: rgba(16, 185, 129, 0.5);
          transform: scale(1.12) rotate(90deg);
        }

        /* Back to Top Button */
        .back-to-top-button {
          position: fixed;
          bottom: 90px;
          right: 24px;
          z-index: 29;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: white;
          padding: 14px;
          border-radius: 50%;
          border: none;
          box-shadow: 0 10px 25px -5px rgba(139, 92, 246, 0.4);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          animation: fadeInUp 0.3s ease-out;
        }

        .back-to-top-button:hover {
          transform: translateY(-4px) scale(1.1);
          background: linear-gradient(135deg, #7c3aed, #6d28d9);
          box-shadow: 0 15px 35px -5px rgba(139, 92, 246, 0.5);
        }

        .back-to-top-button:active {
          transform: translateY(-2px) scale(1.05);
        }

         /* Sticky Categories Bar Enhancement */
         .sticky-categories-bar {
           position: sticky;
           top: 72px;
           z-index: 900; /* Below AppBar (1000) but above content */
           animation: slideDown 0.3s ease-out;
         }

         @media (max-width: 768px) {
           .sticky-categories-bar { top: 64px; }
         }
         @media (max-width: 640px) {
           .sticky-categories-bar { top: 60px; }
         }
         @media (max-width: 480px) {
           .sticky-categories-bar { top: 56px; }
         }

         @keyframes slideDown {
           from {
             opacity: 0;
             transform: translateY(-10px);
           }
           to {
             opacity: 1;
             transform: translateY(0);
           }
         }

         /* Enhanced Category Tab Hover Effects */
         .sticky-categories-bar button:hover {
           transform: translateY(-3px) !important;
         }

         .sticky-categories-bar button:active {
           transform: translateY(-1px) scale(0.98) !important;
         }

         @media (max-width: 640px) {
           .back-to-top-button {
             bottom: 80px;
             right: 16px;
             padding: 12px;
           }

           .filter-chip {
             font-size: 13px;
             padding: 8px 14px;
           }

           .sticky-categories-bar {
             padding: 16px 18px !important;
           }
           
           .sticky-categories-bar button {
             padding: 12px 18px !important;
             font-size: 14px !important;
             gap: 10px !important;
           }
         }
      `}</style>
    </div>
  );
};

export default HomeScreen;
